/** Online admissions: the public form and the school office screens. */

export const ADMISSION_STATUSES = [
  'submitted',
  'reviewing',
  'waitlisted',
  'accepted',
  'rejected',
  'enrolled',
] as const
export type AdmissionStatus = (typeof ADMISSION_STATUSES)[number]

export interface RequiredDocument {
  index: number
  label: string
  description: string | null
  required: boolean
  /** Sent with the form (true) or brought to the school office (false). */
  upload: boolean
}

export interface OfferedClass {
  id: number
  name: string
  level: number
  series: string | null
  description: string | null
  /** null = no limit. */
  placesLeft: number | null
}

/** GET /public/admissions */
export interface PublicAdmissions {
  open: boolean
  academicYear: { id: number; name: string } | null
  opensOn: string | null
  closesOn: string | null
  closedMessage: string | null
  introText: string | null
  classes: OfferedClass[]
  requiredDocuments: RequiredDocument[]
  askPreviousSchool: boolean
  guardiansEnabled: boolean
  upload: { maxBytes: number; types: string[] }
  school: {
    name: string
    shortName: string | null
    logoUrl: string | null
    phone: string | null
    email: string | null
    address: string | null
  }
}

export interface UploadedDocument {
  token: string
  name: string
  size: number
  mimeType: string
  expiresAt: string
}

export interface SubmitResult {
  reference: string
  status: AdmissionStatus
  message: string | null
  email: string | null
}

/** GET /public/admissions/{reference}?email= */
export interface PublicApplicationStatus {
  reference: string
  status: AdmissionStatus
  message: string
  reason: string | null
  student: { firstName: string; lastName: string }
  className: string
  academicYear: string
  sectionName: string | null
  submittedAt: string
  updatedAt: string
}

export interface ApplicationGuardian {
  relation: string
  name: string
  phone: string | null
  email: string | null
  occupation: string | null
  isPrimary: boolean
}

export interface ApplicationSummary {
  id: number
  reference: string
  status: AdmissionStatus
  firstName: string
  lastName: string
  fullName: string
  gender: string
  dateOfBirth: string
  class: { id: number; name: string } | null
  className: string
  academicYear: { id: number | null; name: string }
  guardian: { name: string; phone: string | null; email: string | null; relation: string } | null
  phone: string | null
  documentCount: number
  sectionName: string | null
  studentId: number | null
  submittedAt: string
  updatedAt: string
}

export interface ApplicationEvent {
  id: number
  kind: 'status' | 'note'
  from: AdmissionStatus | null
  to: AdmissionStatus | null
  body: string | null
  author: string | null
  createdAt: string
}

export interface ApplicationDetail extends ApplicationSummary {
  placeOfBirth: string | null
  nationality: string | null
  address: string | null
  email: string | null
  previousSchool: string | null
  previousClass: string | null
  guardians: ApplicationGuardian[]
  documents: {
    index: number
    label: string
    name: string
    mimeType: string
    size: number
    url: string | null
  }[]
  requiredDocuments: RequiredDocument[]
  decisionReason: string | null
  locale: string
  placesLeft: number | null
  transitions: AdmissionStatus[]
  events: ApplicationEvent[]
}

export interface SectionOption {
  id: number
  name: string
  displayName: string
  capacity: number | null
  enrolled: number
  seatsLeft: number | null
}

export interface EnrolResult {
  application: ApplicationDetail
  student: { id: number; fullName: string; studentCode: string }
  portalAccount: { email: string; invited: boolean; existing: boolean } | null
}

export interface AdmissionSettings {
  open: boolean
  isOpenNow: boolean
  academicYear: { id: number; name: string; isCurrent: boolean } | null
  opensOn: string | null
  closesOn: string | null
  classes: {
    classId: number
    name: string
    level: number
    series: string | null
    places: number | null
    description: string | null
    taken: number
    placesLeft: number | null
  }[]
  introText: string | null
  closedMessage: string | null
  confirmationMessage: string | null
  requiredDocuments: Omit<RequiredDocument, 'index'>[]
  notifyEmail: string | null
  askPreviousSchool: boolean
  publicUrl: string
  updatedAt: string | null
}

export interface AdmissionSettingsPayload {
  open: boolean
  academicYearId: number | null
  opensOn: string | null
  closesOn: string | null
  classes: { classId: number; places: number | null; description: string | null }[]
  introText: string | null
  closedMessage: string | null
  confirmationMessage: string | null
  requiredDocuments: Omit<RequiredDocument, 'index'>[]
  notifyEmail: string | null
  askPreviousSchool: boolean
}
